/* ================================================================
   NY2K - LUMEN, MOTION
   Self-contained homepage motion, namespaced and guarded. It does
   not touch the fable-* system, creates no second smooth-scroll, and
   shares nothing global but the .lum-js class it sets on <html>.

   Every hidden initial state lives behind .lum-js, which is set HERE
   and not in the stylesheet. If this script never arrives - a CDN
   fails, a corporate proxy strips it - the page renders fully
   visible instead of blank. That is the entire reason for the class.

   22 Sep 2026 - INVISIBLE HEADINGS FIX
   Every data-lines heading on the page was rendering invisible. The
   CSS parks each line at transform: translateY(108%), and the tweens
   asked GSAP for yPercent: 0. When GSAP reads a transform that CSS
   applied, it parses it into a PIXEL y value, not yPercent - so the
   tween moved yPercent from 0 to 0 and the line never left the
   bottom of its overflow:hidden mask. data-lift worked because it is
   translateY(24px) tweened to y: 0, pixels to pixels.

   Every reveal now uses fromTo with explicit start values for BOTH
   y and yPercent, so nothing depends on how GSAP parses the CSS.
   Apertures get the same treatment for clip-path. And init runs
   inside try/catch: any exception now settles the page visible,
   rather than leaving whatever was mid-reveal hidden for good.
================================================================ */

(function () {
  "use strict";

  var doc = document, html = doc.documentElement;
  html.classList.add("lum-js");

  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var fine   = matchMedia("(hover: hover) and (pointer: fine)").matches;
  var wide   = matchMedia("(min-width: 1000px)");

  var E = { lume: "expo.out", soft: "power3.out", none: "none" };
  var D = { base: .9, slow: 1.2, open: 1.6 };
  var S = { base: .08, loose: .13 };

  /* Start and end states for the masked lines. Both axes are pinned
     explicitly: y clears whatever pixel value GSAP parsed from the
     CSS transform, yPercent does the actual travel. */
  var LINE_FROM = { y: 0, yPercent: 108 };
  var LINE_TO   = { y: 0, yPercent: 0 };
  var APERTURE_FROM = "inset(44% 0% 44% 0%)";
  var APERTURE_TO   = "inset(0% 0% 0% 0%)";

  function settle(el) {
    el.style.transform = "none";
    el.style.opacity = "1";
    el.style.clipPath = "none";
  }
  function settleAll(root) {
    root.querySelectorAll(".lum-mask > span, [data-lift], [data-aperture], .lum-overture-media")
      .forEach(settle);
    root.querySelectorAll(".lum-gauge i").forEach(function (g) {
      g.style.transform = "scaleX(1)";
    });
    /* The sweep paints on the line spans (see lum-polish.css), so the
       fallback has to restore the fill there, not only on the heading. */
    root.querySelectorAll(".lum-sweep, .lum-sweep .lum-mask > span").forEach(function (s) {
      s.style.webkitTextFillColor = "currentColor";
      s.style.backgroundImage = "none";
    });
  }

  /* A CDN failing is a normal state, not an exception. */
  if (!window.gsap || !window.ScrollTrigger) { settleAll(doc); return; }

  if (reduce) { settleAll(doc); return; }

  if (window.__lum) return;
  window.__lum = true;
  gsap.registerPlugin(ScrollTrigger);

  /* Mobile browser chrome collapsing changes the viewport height.
     Without this every one of those changes refreshes all triggers
     mid-scroll and anything pinned or scrubbed visibly jumps. */
  ScrollTrigger.config({ ignoreMobileResize: true });

  /* ---------- overture ----------
     The aperture opens, the headline rises out of it, the rest fades
     up behind. Plays once, on load, on the only screen where a
     visitor will wait for something. */
  function overture(root) {
    if (root.__lum) return;
    root.__lum = true;

    var media = root.querySelector(".lum-overture-media");
    var lines = root.querySelectorAll(".lum-mask > span");
    var lifts = root.querySelectorAll("[data-lift]");
    var tl = gsap.timeline({ defaults: { ease: E.lume } });

    if (media) {
      tl.fromTo(media,
        { clipPath: "inset(42% 0% 42% 0%)", scale: 1.12 },
        { clipPath: "inset(0% 0% 0% 0%)", scale: 1, duration: D.open });
    }
    if (lines.length) {
      tl.fromTo(lines, LINE_FROM,
        { y: 0, yPercent: 0, duration: 1.4, stagger: S.loose }, "-=1.15");
    }
    if (lifts.length) {
      tl.fromTo(lifts, { y: 24, opacity: 0 },
        { y: 0, opacity: 1, duration: D.base, ease: E.soft, stagger: S.base }, "-=0.8");
    }

    /* Slow drift as the leaf leaves. Desktop only: on a phone this
       competes with the browser chrome collapsing. */
    if (media && fine) {
      gsap.to(media, { yPercent: 9, ease: E.none, scrollTrigger: {
        trigger: root, start: "top top", end: "bottom top", scrub: true } });
    }
  }

  /* ---------- masked line reveals ---------- */
  function lines(root) {
    root.querySelectorAll("[data-lines]").forEach(function (h) {
      if (h.__lum || h.closest(".lum-overture")) return;
      h.__lum = true;
      var spans = h.querySelectorAll(".lum-mask > span");
      if (!spans.length) return;
      gsap.fromTo(spans, LINE_FROM, {
        y: 0, yPercent: 0, duration: D.slow, ease: E.lume, stagger: S.loose,
        scrollTrigger: { trigger: h, start: "top 84%", once: true }
      });
    });
  }

  /* ---------- the sweep ----------
     A class, not a tween: the highlight is painted with
     background-clip on the text itself, and GSAP cannot animate a
     background-clip gradient reliably across browsers. CSS owns the
     keyframe; this only decides when it fires.

     Delayed a beat behind the line reveal so the light crosses type
     that has already arrived, rather than racing it. */
  function sweeps(root) {
    root.querySelectorAll(".lum-sweep").forEach(function (el) {
      if (el.__lum) return;
      el.__lum = true;
      ScrollTrigger.create({
        trigger: el, start: "top 78%", once: true,
        onEnter: function () {
          gsap.delayedCall(.45, function () { el.classList.add("is-lit"); });
        }
      });
    });
  }

  /* ---------- apertures ----------
     clip-path is the sanctioned exception to transform and opacity:
     it composites, and there is no transform equivalent for a band
     opening from its own centre. fromTo with identical inset syntax
     on both ends, so the interpolation cannot mis-pair values. */
  function apertures(root) {
    root.querySelectorAll("[data-aperture]").forEach(function (el) {
      if (el.__lum) return;
      el.__lum = true;
      gsap.fromTo(el, { clipPath: APERTURE_FROM }, {
        clipPath: APERTURE_TO, duration: 1.35, ease: E.lume,
        scrollTrigger: { trigger: el, start: "top 86%", once: true }
      });
    });
  }

  /* ---------- lifts ----------
     Batched. One trigger per element would be fifty triggers on this
     page, each re-measuring on every refresh, which is where scroll
     jank starts. */
  function lifts(root) {
    var items = Array.prototype.filter.call(
      root.querySelectorAll("[data-lift]"),
      function (el) { return !el.closest(".lum-overture"); }
    );
    if (!items.length) return;
    gsap.set(items, { y: 24, opacity: 0 });
    ScrollTrigger.batch(items, {
      start: "top 90%",
      onEnter: function (b) {
        gsap.to(b, { y: 0, opacity: 1, duration: D.base, ease: E.soft,
          stagger: S.base, overwrite: true });
      }
    });
  }

  /* ---------- the tray ----------
     Pinned viewport, horizontal track. Desktop only: on touch the
     rail stays a native scroll-snap carousel, which behaves better
     and is what a thumb expects.

     The pin starts at "top top" and the scroll length equals the
     horizontal travel, so the section is pinned before the track
     moves. Starting anywhere else shows half a card. */
  function tray(root) {
    root.querySelectorAll("[data-tray]").forEach(function (stage) {
      if (stage.__lum) return;
      stage.__lum = true;

      var rail  = stage.querySelector(".lum-rail");
      var gauge = stage.parentElement.querySelector(".lum-gauge i");
      if (!rail) return;

      var pan;
      var build = function () {
        if (pan) {
          if (pan.scrollTrigger) pan.scrollTrigger.kill(true);
          pan.kill();
          pan = null;
        }
        stage.classList.remove("lum-tray--panned");
        gsap.set(rail, { x: 0 });

        if (!wide.matches) {
          /* Touch path: the gauge tracks the native scroll instead. */
          if (gauge) {
            var upd = function () {
              var max = rail.scrollWidth - rail.clientWidth;
              gsap.set(gauge, { scaleX: max > 0 ? .06 + (rail.scrollLeft / max) * .94 : 1 });
            };
            rail.addEventListener("scroll", upd, { passive: true });
            upd();
          }
          return;
        }

        stage.classList.add("lum-tray--panned");
        var travel = rail.scrollWidth - stage.clientWidth;
        if (travel <= 0) return;

        pan = gsap.to(rail, {
          x: -travel, ease: E.none,
          scrollTrigger: {
            trigger: stage,
            start: "top top",
            end: function () { return "+=" + travel; },
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
            onUpdate: function (self) {
              if (gauge) gsap.set(gauge, { scaleX: .06 + self.progress * .94 });
            }
          }
        });
      };

      build();
      /* Rebuilt rather than refreshed on breakpoint change: the
         desktop and touch paths are different mechanisms, not two
         sizes of one. */
      wide.addEventListener("change", build);
    });
  }

  /* ---------- the commission stack ----------
     A real sticky stack. Every card but the last recedes as the next
     arrives, driven by the NEXT card's trigger so it shrinks exactly
     in step with its successor.

     Sticky positioning does the pinning in CSS, so this owns only
     the recede. That keeps the stack working when the script is
     absent, and avoids a pin-spacer per card. */
  function stack(root) {
    root.querySelectorAll("[data-stack]").forEach(function (wrap) {
      if (wrap.__lum) return;
      wrap.__lum = true;
      var cards = wrap.querySelectorAll(".lum-step");
      if (cards.length < 2) return;

      cards.forEach(function (card, i) {
        if (i === cards.length - 1) return;
        gsap.to(card, {
          scale: .94, opacity: .45, ease: E.none,
          scrollTrigger: {
            trigger: cards[i + 1],
            start: "top bottom",
            end: "top top",
            scrub: true
          }
        });
      });
    });
  }

  function init(root) {
    try {
      root.querySelectorAll(".lum-overture").forEach(overture);
      lines(root); sweeps(root); apertures(root);
      lifts(root); tray(root); stack(root);
    } catch (err) {
      /* Anything mid-reveal when the exception hit would otherwise stay
         hidden permanently. A page that shows without its motion is a
         degraded page; a page with invisible headings is a broken one. */
      if (window.console) console.warn("[lum] motion init failed, settling visible:", err);
      settleAll(root);
    }
  }

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", function () { init(doc); });
  } else { init(doc); }

  /* Trigger positions are measured before Cormorant swaps in. The
     swap changes the height of every heading and shifts every start
     below it, so without this the first reveals fire early and only
     correct themselves on the next resize. */
  addEventListener("load", function () { ScrollTrigger.refresh(); });
  if (doc.fonts && doc.fonts.ready) {
    doc.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }

  /* ---------- theme editor ----------
     A reloaded section arrives with the hidden states applied and no
     triggers attached, so without this it stays invisible while the
     client is editing. kill(true) reverts, which also clears the
     pin-spacer instead of stacking a dead one on every reload. */
  doc.addEventListener("shopify:section:load", function (e) {
    var t = e.target;
    if (!t.querySelector(".lum")) return;
    ScrollTrigger.getAll().forEach(function (st) {
      if (st.trigger && t.contains(st.trigger)) st.kill(true);
    });
    t.querySelectorAll("[data-lines], [data-aperture], [data-tray], [data-stack], .lum-sweep, .lum-overture")
      .forEach(function (el) { el.__lum = false; });
    init(t);
    ScrollTrigger.refresh();
  });
  doc.addEventListener("shopify:section:unload", function () { ScrollTrigger.refresh(); });
})();
